import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Analytics,
  Refresh
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Bar, BarChart } from 'recharts';
import { supabase } from '../../services/supabaseClient';
import axios from 'axios';

const CostManagement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const response = await axios.get('/api/admin/cost-estimates', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const d = data || {};
  const currentCost = d.currentMonth?.cost || 0;
  const lastCost = d.lastMonth?.cost || 0;
  const currentChars = d.currentMonth?.characters || 0;
  const currentWords = d.currentMonth?.words || 0;
  const totalCost = d.totalCost || 0;
  const totalChars = d.totalCharacters || 0;
  const costTrend = currentCost >= lastCost ? 'up' : 'down';
  const costChange = lastCost > 0 ? Math.abs(((currentCost - lastCost) / lastCost) * 100) : 0;
  const monthlyTrend = d.monthlyTrend || [];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Cost Estimates
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchCosts} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Costs are <strong>estimated</strong> based on Google Translate API pricing ($20 / 1M characters). Check your Google Cloud Console for actual billing.
      </Alert>

      <Grid container spacing={3}>
        {/* Current Month */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoney color="primary" />
                <Typography variant="h6" ml={1}>This Month</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                ${currentCost.toFixed(2)}
              </Typography>
              <Box display="flex" alignItems="center" mt={1}>
                {costTrend === 'up' ? (
                  <TrendingUp color="error" sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingDown color="success" sx={{ fontSize: 16 }} />
                )}
                <Typography
                  variant="body2"
                  color={costTrend === 'up' ? 'error.main' : 'success.main'}
                  ml={0.5}
                >
                  {costChange.toFixed(1)}% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Last Month */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoney color="secondary" />
                <Typography variant="h6" ml={1}>Last Month</Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                ${lastCost.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                {(d.lastMonth?.characters || 0).toLocaleString()} characters
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Characters This Month */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Analytics color="info" />
                <Typography variant="h6" ml={1}>This Month</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {currentChars > 1000000
                  ? (currentChars / 1000000).toFixed(2) + 'M'
                  : currentChars.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                characters translated
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {currentWords.toLocaleString()} words
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* All-Time Total */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUp color="warning" />
                <Typography variant="h6" ml={1}>All-Time</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                ${totalCost.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {totalChars > 1000000
                  ? (totalChars / 1000000).toFixed(2) + 'M chars'
                  : totalChars.toLocaleString() + ' chars'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Cost Trend Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Cost Trend (est.)
              </Typography>
              {monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => [`$${value}`, 'Est. Cost']} />
                    <Line
                      type="monotone"
                      dataKey="estimatedCost"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ fill: '#1976d2' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">No data yet</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Character Volume Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Characters
              </Typography>
              {monthlyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => [value.toLocaleString(), 'Characters']} />
                    <Bar dataKey="characters" fill="#9c27b0" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">No data yet</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Breakdown Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Breakdown
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Characters</TableCell>
                      <TableCell align="right">Words</TableCell>
                      <TableCell align="right">Est. Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...monthlyTrend].reverse().map((m) => (
                      <TableRow key={m.monthKey}>
                        <TableCell>{m.month}</TableCell>
                        <TableCell align="right">{m.characters.toLocaleString()}</TableCell>
                        <TableCell align="right">{m.words.toLocaleString()}</TableCell>
                        <TableCell align="right">${m.estimatedCost.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {d.timestamp && (
        <Typography variant="caption" color="textSecondary" mt={2} display="block">
          Last updated: {new Date(d.timestamp).toLocaleTimeString()}
        </Typography>
      )}
    </Box>
  );
};

export default CostManagement;
